import { Form, type FormikProps } from "formik";
import React, { useState } from "react";

import { Button2 } from "@/components/UI2/atoms/Button";
import { cn } from "@/lib/helpers/cn";
import { CustomForm } from "@/component-library/CustomForm";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import FormikInput from "@/components/UI/FormikInput";
import FormikMobileInput from "@/components/UI/formikMobileInput";
import {
  PATIENT_BASIC_SCHEMA,
  PREFERRED_NAME_SCHEMA_PATIENT,
} from "@/containers/Dashboard/Patient/schema/PatientProfileInfoSchema";
import { PATIENT_VALIDATION_SCHEMA } from "@/containers/Dashboard/Patient/schema/PatientProfileValidation";
import type { PatientProfile } from "@/core/types";
import { renderTitleWithValue } from "@/components/Patient/Profile/renderTitleWithValue";

import type { UpdatePatientProfileVariables } from "../api/queryFunctions/updatePatientProfile";

type Props = {
  data: PatientProfile;
  submit: (
    data: UpdatePatientProfileVariables,
    actions: { setSubmitting: (isSubmitting: boolean) => void },
  ) => void;
};

export function BasicInfoSection({ data, submit }: Props) {
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = (
    formData: UpdatePatientProfileVariables,
    actions: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    setIsEditing(false);
    submit(formData, actions);
  };

  const renderForm = (formikProps: FormikProps<UpdatePatientProfileVariables>) => (
    <Form>
      <div className="mx-auto px-0">
        <div className="container px-0">
          <div className="row">
            {PATIENT_BASIC_SCHEMA.map((formEl) => {
              if (formEl.elementType === "segment") {
                return (
                  <h6 className="col-12" key={formEl.displayName}>
                    <b>{formEl.displayName}</b>
                  </h6>
                );
              }

              if (formEl.elementType === "readOnly") {
                return (
                  <div className="col-12 col-lg-6" key={formEl.name}>
                    <FormikInput
                      disabled
                      errors={formikProps.errors}
                      formEl={formEl}
                      inputBorderDisabled="border-0 px-0"
                      touched={formikProps.touched}
                    />
                  </div>
                );
              }

              return (
                <div className="col-12 col-lg-6" key={formEl.name}>
                  {formEl.name === "email" ? (
                    <FormikInput
                      disabled
                      errors={formikProps.errors}
                      formEl={formEl}
                      inputBorderDisabled="border-0 px-0"
                      touched={formikProps.touched}
                    />
                  ) : formEl.name === "mobile" ? (
                    <FormikMobileInput
                      errors={formikProps.errors}
                      formEl={formEl}
                      onChange={async (val: { value: string }) => {
                        await formikProps.setFieldValue("mobile", val.value);
                        formikProps.setFieldTouched("mobile");
                      }}
                      touched={formikProps.touched}
                      value={formikProps.values.mobile}
                    />
                  ) : formEl.name === "dob" ? (
                    <FormikMobileInput
                      errors={formikProps.errors}
                      formEl={formEl}
                      onChange={async (val: { value: string }) => {
                        await formikProps.setFieldValue("dob", val.value);
                        formikProps.setFieldTouched("dob");
                      }}
                      touched={formikProps.touched}
                      value={formikProps.values.dob}
                    />
                  ) : (
                    <FormikInput
                      errors={formikProps.errors}
                      formEl={formEl}
                      touched={formikProps.touched}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="tw:flex tw:justify-end tw:gap-3">
        <Button2 onPress={() => setIsEditing(false)} size="md" type="button" variant="secondary">
          Cancel
        </Button2>

        <Button2
          isDisabled={formikProps.isSubmitting}
          isLoading={formikProps.isSubmitting}
          size="md"
          type="submit"
          variant="primary"
        >
          Save
        </Button2>
      </div>
    </Form>
  );

  return (
    <DashboardCard2
      Actions={
        <Button2 isDisabled={isEditing} onPress={() => setIsEditing(true)} variant="link">
          Edit
        </Button2>
      }
      title="Basic Information"
    >
      <div className={cn("tw:grid tw:gap-5", !isEditing && "tw:md:grid-cols-2")}>
        {!isEditing &&
          readItems(data).map((item) => (
            <div className="" key={item.title}>
              {renderTitleWithValue(item.title, item.value, item.subtitle, item.optional)}
            </div>
          ))}

        {isEditing && (
          <CustomForm
            className="tw:contents"
            initialValues={data}
            onSubmit={handleSubmit}
            render={renderForm}
            validationSchema={PATIENT_VALIDATION_SCHEMA}
          />
        )}
      </div>
    </DashboardCard2>
  );
}

const readItems = (data: PatientProfile) => [
  { title: "Legal First Name", value: data.first_name || "" },
  { title: "Legal Last Name", value: data.last_name || "" },
  {
    optional: true,
    subtitle: PREFERRED_NAME_SCHEMA_PATIENT.elementConfig.subtext,
    title: "Nickname",
    value: data.preferred_name || "",
  },
  { optional: true, title: "Preferred Pronouns", value: data.preferred_pronouns || "" },
  { title: "Date of Birth", value: data.dob || "" },
  { title: "Gender", value: data.gender || "" },
  { title: "Mobile", value: data.mobile || "" },
  { title: "Email", value: data.email || "" },
  { title: "Street", value: data.address.street || "" },
  { title: "State", value: data.address.state || "" },
  { title: "City", value: data.address.city || "" },
  { title: "Zip", value: data.address.zip_code || "" },
];
